/**
 * System Info — deployment image and release alignment (full page at /system-info).
 */

import { useEffect, useState, type JSX } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Link,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import WarningIcon from '@material-ui/icons/Warning';
import { Progress } from '@backstage/core-components';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useAdminGroupMember } from '../devtools/AdminGroupMemberContext';

type ReleaseConfig = {
  releaseVersion: string;
  prodVersion: string;
};

type SystemInfoResponse = {
  environment: string;
  runningImage: string;
  runningTag: string;
  appVersion: string;
  releaseConfig: ReleaseConfig | null;
  releaseConfigValid: boolean;
  plannedVersion: string | null;
  status: 'ok' | 'deviating' | 'advisory' | 'unknown';
  summary: string;
  privileged: boolean;
  idealTags: string[];
  latestMainAppHash: string | null;
  latestMainGitCommitSha: string | null;
  localMainGitCommitSha?: string | null;
  gitlabMainGitCommitSha?: string | null;
  deviation: {
    isDeviating: boolean;
    reasons: string[];
    recommendations: string[];
  } | null;
};

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  list: {
    margin: theme.spacing(1, 0, 0, 0),
    paddingLeft: theme.spacing(2.5),
  },
}));

function StatusIcon({ status }: { status: SystemInfoResponse['status'] }) {
  switch (status) {
    case 'ok':
      return <CheckCircleIcon fontSize="small" color="primary" />;
    case 'deviating':
      return <ErrorIcon fontSize="small" color="error" />;
    case 'advisory':
      return <WarningIcon fontSize="small" color="action" />;
    default:
      return <InfoOutlinedIcon fontSize="small" color="action" />;
  }
}

function envLabel(env: string): string {
  switch (env) {
    case 'local':
      return 'Local';
    case 'dev':
      return 'Development';
    case 'stage':
      return 'Staging';
    case 'prod':
      return 'Production';
    default:
      return env || 'Unknown';
  }
}

export function IronPhoenixSystemInfo({
  variant = 'page',
}: {
  /** page = dedicated /system-info route (no duplicate in-card title) */
  variant?: 'page' | 'compact';
}): JSX.Element {
  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);
  const admin = useAdminGroupMember();
  const [data, setData] = useState<SystemInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const backend = configApi.getString('backend.baseUrl').replace(/\/$/, '');
    const url = `${backend}/api/system-info`;
    const maxAttempts = 4;
    const perAttemptTimeoutMs = 45_000;

    const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

    (async () => {
      let lastMessage = 'Failed to load system info';
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (cancelled) {
          return;
        }
        const controller = new AbortController();
        const timeoutId = window.setTimeout(
          () => controller.abort(),
          perAttemptTimeoutMs,
        );
        try {
          const r = await fetchApi.fetch(url, {
            credentials: 'include',
            signal: controller.signal,
          });
          window.clearTimeout(timeoutId);
          if (r.ok) {
            const body = (await r.json()) as SystemInfoResponse;
            if (!cancelled) {
              setData(body);
              setError(null);
              setLoading(false);
            }
            return;
          }
          lastMessage = `System info unavailable (${r.status})`;
          const retryable = r.status >= 500 && r.status < 600;
          if (retryable && attempt < maxAttempts) {
            await sleep(350 * attempt);
            continue;
          }
          break;
        } catch (e: unknown) {
          window.clearTimeout(timeoutId);
          const err = e instanceof Error ? e : new Error(String(e));
          const aborted =
            err.name === 'AbortError' ||
            /aborted|AbortError|timeout/i.test(err.message);
          lastMessage = aborted
            ? 'System info request timed out or was cancelled'
            : err.message || lastMessage;
          if (aborted && attempt < maxAttempts) {
            await sleep(400 * attempt);
            continue;
          }
          if (attempt < maxAttempts) {
            await sleep(400 * attempt);
            continue;
          }
          break;
        }
      }
      if (!cancelled) {
        setError(lastMessage);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchApi, configApi]);

  if (loading) {
    return (
      <Box className={classes.root} data-testid="iron-phoenix-system-info">
        <Progress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box className={classes.root} data-testid="iron-phoenix-system-info">
        <Typography variant="subtitle2" color="textSecondary">
          System info
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {error ?? 'Unavailable'}
        </Typography>
      </Box>
    );
  }

  const showPrivileged = admin === true && data.privileged && data.deviation;
  const cfg = data.releaseConfig;
  /** From app-config (`ironPhoenix.systemInfo.gitlab.projectPath` / IRONPHOENIX_GITLAB_PROJECT_PATH). */
  const gitlabProjectPath = configApi.getOptionalString(
    'ironPhoenix.systemInfo.gitlab.projectPath',
  );

  const renderPrivilegedSection = (): JSX.Element | null => {
    if (showPrivileged && data.deviation) {
      const { reasons, recommendations } = data.deviation;
      return (
        <Collapse in>
          {reasons.length > 0 ? (
            <>
              <Typography variant="body2" style={{ marginTop: 8, fontWeight: 600 }}>
                Deviation (privileged)
              </Typography>
              <ul className={classes.list}>
                {reasons.map((r, i) => (
                  <li key={i}>
                    <Typography variant="body2">{r}</Typography>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {recommendations.length > 0 ? (
            <>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                Recommended action
              </Typography>
              <ul className={classes.list}>
                {recommendations.map((r, i) => (
                  <li key={i}>
                    <Typography variant="body2">{r}</Typography>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {data.idealTags.length > 0 ? (
            <Typography variant="caption" color="textSecondary" display="block">
              Ideal tags: {data.idealTags.join(', ')}
            </Typography>
          ) : null}
          {!data.releaseConfigValid && cfg ? (
            <Typography variant="caption" color="error" display="block">
              release-config.yaml failed validation — fix before release pipelines.
            </Typography>
          ) : null}
        </Collapse>
      );
    }
    if (admin !== true && data.environment === 'prod') {
      return (
        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
          Production should always run the latest image in the prod container registry.
        </Typography>
      );
    }
    return null;
  };

  return (
    <Box className={classes.root} data-testid="iron-phoenix-system-info">
      <div className={classes.titleRow}>
        <StatusIcon status={data.status} />
        {variant === 'compact' ? (
          <Typography variant="subtitle1" component="h2">
            <strong>System info</strong>
          </Typography>
        ) : null}
        <Chip size="small" label={envLabel(data.environment)} />
        {data.plannedVersion ? (
          <Chip size="small" variant="outlined" label={`Planned: ${data.plannedVersion}`} />
        ) : null}
      </div>

      <Typography variant="body2">{data.summary}</Typography>

      <div className={classes.meta}>
        {data.runningTag ? (
          <Chip size="small" label={`Image: ${data.runningTag}`} variant="outlined" />
        ) : null}
        {data.environment === 'local' ? (
          <Chip size="small" label="Local deployment" color="default" variant="outlined" />
        ) : null}
        {cfg?.prodVersion && data.environment !== 'local' ? (
          <Chip size="small" label={`Prod line: ${cfg.prodVersion}`} variant="outlined" />
        ) : null}
      </div>

      {renderPrivilegedSection()}

      {data.latestMainAppHash ? (
        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
          Latest main app hash: <strong>{data.latestMainAppHash}</strong>
          {data.latestMainGitCommitSha ? (
            <>
              {' '}
              (GitLab main{' '}
              {gitlabProjectPath ? (
                <Link
                  href={`https://gitlab.com/${gitlabProjectPath}/-/commit/${data.latestMainGitCommitSha}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {data.latestMainGitCommitSha.slice(0, 12)}
                </Link>
              ) : (
                <strong>{data.latestMainGitCommitSha.slice(0, 12)}</strong>
              )}
              )
            </>
          ) : null}
          {data.localMainGitCommitSha &&
          data.gitlabMainGitCommitSha &&
          data.localMainGitCommitSha !== data.gitlabMainGitCommitSha ? (
            <span>
              {' '}
              — local clone main is{' '}
              <strong>{data.localMainGitCommitSha.slice(0, 12)}</strong> (behind GitLab)
            </span>
          ) : null}
        </Typography>
      ) : null}
    </Box>
  );
}
